/* global BigInt */
import React, {
  createContext, useState, useEffect,
  useRef, useCallback, useMemo,
} from 'react'
import pako from 'pako'
import QRCode from 'react-qr-code'
import {
  encodeForQR, decodeFromQR, estimateEncodedSize, QR_SAFE_LIMIT,
  generateSessionKey, deriveICECredentials,
  extractCandidateFromSDP, extractFingerprintFromSDP,
  reconstructAnswerSDP, extractMidsFromSDP, extractMlinesFromSDP,
} from './sdpCompact'
import { createPeerConnection, waitForICEGathering, logConnectionDiagnostics } from './iceConfig'

// ─────────────────────────────────────────────────────────────────────────────
// Answer encode/decode — vB: credential-aware binary packing
// ─────────────────────────────────────────────────────────────────────────────

const safeB64Encode = (uint8) => {
  let binary = ''
  const CHUNK = 8192
  for (let i = 0; i < uint8.length; i += CHUNK)
    binary += String.fromCharCode(...uint8.subarray(i, i + CHUNK))
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

const safeB64Decode = (str) => {
  const padded = str.replace(/-/g, '+').replace(/_/g, '/')
  const pad    = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4))
  return Uint8Array.from(atob(padded + pad), (c) => c.charCodeAt(0))
}

const hexToBytes = (hex) => {
  const clean = (hex || '').replace(/:/g, '')
  if (!clean || clean.length % 2 !== 0) return new Uint8Array(0)
  const pairs = clean.match(/.{2}/g)
  return pairs ? new Uint8Array(pairs.map(h => parseInt(h, 16))) : new Uint8Array(0)
}

const bytesToHex = (bytes) =>
  Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('')

const credToBytes = (str) => {
  if (str && /^[0-9a-fA-F]+$/.test(str) && str.length % 2 === 0)
    return { bytes: hexToBytes(str), isHex: true }
  return { bytes: new TextEncoder().encode(str || ''), isHex: false }
}

export const encodeAnswer = (ip, port, fingerprintHex, ufrag, pwd) => {
  try {
    const ipParts = (ip || '').split('.').map(n => parseInt(n, 10))
    if (ipParts.length !== 4 || ipParts.some(n => isNaN(n) || n < 0 || n > 255)) {
      console.error('[encodeAnswer] Invalid IP:', ip); return null
    }
    const portNum = parseInt(port, 10)
    if (isNaN(portNum) || portNum < 1 || portNum > 65535) {
      console.error('[encodeAnswer] Invalid port:', port); return null
    }

    const u  = credToBytes(ufrag || '')
    const p  = credToBytes(pwd   || '')
    const fp = hexToBytes((fingerprintHex || '').replace(/:/g, ''))

    const totalSize = 1 + 4 + 2 + 1 + u.bytes.length + 1 + p.bytes.length + 32
    const packed    = new Uint8Array(totalSize)
    let o = 0
    packed[o++] = (u.isHex ? 0x01 : 0) | (p.isHex ? 0x02 : 0)
    ipParts.forEach(b => { packed[o++] = b })
    packed[o++] = (portNum >> 8) & 0xFF
    packed[o++] = portNum & 0xFF
    packed[o++] = u.bytes.length
    u.bytes.forEach(b => { packed[o++] = b })
    packed[o++] = p.bytes.length
    p.bytes.forEach(b => { packed[o++] = b })
    for (let i = 0; i < 32; i++) packed[o++] = fp[i] ?? 0

    return `vB_${safeB64Encode(packed)}`
  } catch (e) { console.error('[encodeAnswer] failed:', e); return null }
}

export const decodeAnswer = (encoded) => {
  if (!encoded) return null
  try {
    const underscoreIdx = encoded.indexOf('_')
    const version = encoded.startsWith('v') && underscoreIdx > 0
      ? encoded.slice(1, underscoreIdx) : null
    const b64part = version ? encoded.slice(underscoreIdx + 1) : encoded

    if (version === 'B') {
      const bytes = safeB64Decode(b64part)
      let o = 0
      const flags    = bytes[o++]
      const ufragHex = !!(flags & 0x01)
      const pwdHex   = !!(flags & 0x02)
      const ip       = `${bytes[o++]}.${bytes[o++]}.${bytes[o++]}.${bytes[o++]}`
      const port     = (bytes[o++] << 8) | bytes[o++]
      const ufragLen = bytes[o++]
      const ufragRaw = bytes.slice(o, o + ufragLen); o += ufragLen
      const pwdLen   = bytes[o++]
      const pwdRaw   = bytes.slice(o, o + pwdLen);   o += pwdLen
      const ufrag    = ufragHex ? bytesToHex(ufragRaw) : new TextDecoder().decode(ufragRaw)
      const pwd      = pwdHex   ? bytesToHex(pwdRaw)   : new TextDecoder().decode(pwdRaw)
      const fingerprintHex = bytesToHex(bytes.slice(o, o + 32)).toUpperCase()
      return { ip, port, fingerprintHex, ufrag, pwd }
    }

    if (version === '9') {
      const bytes = safeB64Decode(b64part)
      let o = 0
      const ip       = `${bytes[o++]}.${bytes[o++]}.${bytes[o++]}.${bytes[o++]}`
      const port     = (bytes[o++] << 8) | bytes[o++]
      const ufragLen = bytes[o++]
      const ufrag    = new TextDecoder().decode(bytes.slice(o, o + ufragLen)); o += ufragLen
      const pwdLen   = bytes[o++]
      const pwd      = new TextDecoder().decode(bytes.slice(o, o + pwdLen));   o += pwdLen
      const fingerprintHex = bytesToHex(bytes.slice(o, o + 32)).toUpperCase()
      return { ip, port, fingerprintHex, ufrag, pwd }
    }

    if (version === '8') {
      const bytes = safeB64Decode(b64part)
      if (bytes.length < 58) throw new Error(`v8 too short: ${bytes.length}`)
      const ip             = `${bytes[0]}.${bytes[1]}.${bytes[2]}.${bytes[3]}`
      const port           = (bytes[4] << 8) | bytes[5]
      const ufrag          = bytesToHex(bytes.slice(6,  10))
      const pwd            = bytesToHex(bytes.slice(10, 26))
      const fingerprintHex = bytesToHex(bytes.slice(26, 58)).toUpperCase()
      return { ip, port, fingerprintHex, ufrag, pwd }
    }

    const packed = pako.inflate(safeB64Decode(b64part))
    let offset = 0
    const header      = packed[offset++]
    const networkType = (header >> 6) & 0x03
    let ip
    if      (networkType === 0) { ip = `192.168.${packed[offset++]}.${packed[offset++]}` }
    else if (networkType === 1) { ip = `10.${packed[offset++]}.${packed[offset++]}.${packed[offset++]}` }
    else if (networkType === 2) { ip = `172.${packed[offset++]}.${packed[offset++]}.${packed[offset++]}` }
    else                        { ip = `${packed[offset++]}.${packed[offset++]}.${packed[offset++]}.${packed[offset++]}` }
    const port     = (packed[offset] << 8) | packed[offset + 1]; offset += 2
    const ufragLen = packed[offset++]
    const ufrag    = new TextDecoder().decode(packed.slice(offset, offset + ufragLen)); offset += ufragLen
    const pwdLen   = packed[offset++]
    const pwd      = new TextDecoder().decode(packed.slice(offset, offset + pwdLen));   offset += pwdLen
    let fingerprintHex = null
    if (offset < packed.length) {
      const fpLen = packed[offset++]
      if (fpLen > 0) {
        const fpData = packed.slice(offset, offset + fpLen); offset += fpLen
        fingerprintHex = version === '7'
          ? bytesToHex(fpData).toUpperCase()
          : new TextDecoder().decode(fpData)
      }
    }
    return { ip, port, fingerprintHex, ufrag, pwd }
  } catch (e) { console.error('[decodeAnswer] failed:', e); return null }
}

export const MODES = {
  STUDIO:          'studio',
  TELEPROMPTER:    'teleprompter',
  REMOTE_SCROLLER: 'remote_scroller',
}

export const StudioContext = createContext({})

const DELAYS = {
  BC_ECHO:         80,
  SEND_FULL_STATE: 400,
  RENEGOTIATE:     300,
  POLL_INTERVAL:   500,
  POLL_MAX:        60,
  ERROR_CLEAR:     8000,
}

const compress = (obj) => {
  try {
    const deflated = pako.deflate(JSON.stringify(obj), { level: 6 })
    let binary = ''
    const chunk = 8192
    for (let i = 0; i < deflated.length; i += chunk)
      binary += String.fromCharCode.apply(null, deflated.subarray(i, i + chunk))
    return btoa(binary)
  } catch (e) { console.warn('[compress] failed:', e.message); return null }
}

const decompress = (str) => {
  try {
    const bin   = atob(str.trim())
    const bytes = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
    const inflated = pako.inflate(bytes)
    let result = ''
    const chunk = 8192
    for (let i = 0; i < inflated.length; i += chunk)
      result += String.fromCharCode.apply(null, inflated.subarray(i, i + chunk))
    return JSON.parse(result)
  } catch (e) { console.warn('[decompress] failed:', e.message); return null }
}

const detectAndDecode = (encoded) => {
  const trimmed = encoded.trim()
  if (trimmed.startsWith('o:') || trimmed.startsWith('a:')) {
    const sdpType   = trimmed.startsWith('a:') ? 'answer' : 'offer'
    const qrDecoded = decodeFromQR(trimmed.slice(2))
    if (qrDecoded && qrDecoded.startsWith('v=')) return { sdp: qrDecoded, type: sdpType, isQRFormat: true }
  }
  const qrDecoded = decodeFromQR(trimmed)
  if (qrDecoded && qrDecoded.startsWith('v=')) return { sdp: qrDecoded, isQRFormat: true }
  const legacy = decompress(trimmed)
  if (legacy && legacy.sdp) return { sdp: legacy.sdp, type: legacy.type, isQRFormat: false }
  return null
}

const waitBothChannels = (dc, cdc) => new Promise(resolve => {
  let dcOpen = false, cdcOpen = false
  const check = () => { if (dcOpen && cdcOpen) resolve() }
  if (dc.readyState === 'open')          { dcOpen  = true } else dc.addEventListener('open',  () => { dcOpen  = true; check() })
  if (!cdc || cdc.readyState === 'open') { cdcOpen = true } else cdc.addEventListener('open', () => { cdcOpen = true; check() })
  check()
  setTimeout(() => {
    if (dcOpen && !cdcOpen) {
      console.warn('[waitBothChannels] cdc did not open within 3s — proceeding with dc only')
      resolve()
    }
  }, 3000)
})

// ─── GuestHandshake ───────────────────────────────────────────────────────────
export const GuestHandshake = ({ encodedOffer, guestMode, onConnected }) => {
  const [status,    setStatus]    = useState('generating')
  const [wordCode,  setWordCode]  = useState('')
  const [answerUrl, setAnswerUrl] = useState('')
  const [copied,    setCopied]    = useState(false)
  const [showQR,    setShowQR]    = useState(false)
  const [errorMsg,  setErrorMsg]  = useState('')
  const [shared,    setShared]    = useState(false)

  const onConnectedRef = useRef(onConnected)
  useEffect(() => { onConnectedRef.current = onConnected }, [onConnected])

  const modeLabel = {
    teleprompter:    'Teleprompter',
    remote_scroller: 'Remote Scroller',
  }[guestMode] || guestMode

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      const decoded = detectAndDecode(encodedOffer)
      if (!decoded) { setErrorMsg('Invalid connection link — ask Studio to generate a new one.'); setStatus('error'); return }

      try {
        const pc = createPeerConnection()

        // Add connection diagnostics
        pc.oniceconnectionstatechange = () => {
          console.log(`[Guest ICE] ${pc.iceConnectionState}`)
          if (pc.iceConnectionState === 'connected') {
            logConnectionDiagnostics(pc, 'Guest connected')
          }
        }

        pc.ondatachannel = (event) => {
          const dc = event.channel
          const handleOpen = () => {
            if (cancelled) return
            onConnectedRef.current?.({ pc, dc })
          }
          if (dc.readyState === 'open') { handleOpen() }
          else { dc.onopen = handleOpen }
        }

        pc.onconnectionstatechange = () => {
          if (!cancelled && pc.connectionState === 'failed') { try { pc.restartIce() } catch {} }
        }

        const sdpType = decoded.type || 'offer'
        await pc.setRemoteDescription(new RTCSessionDescription({ type: sdpType, sdp: decoded.sdp }))
        const answer = await pc.createAnswer()
        await pc.setLocalDescription(answer)
        await waitForICEGathering(pc)

        if (cancelled) { try { pc.close() } catch {}; return }

        const localSDP  = pc.localDescription.sdp
        const candidate = extractCandidateFromSDP(localSDP)
        const fpHex     = extractFingerprintFromSDP(localSDP)
        const ufragLine = localSDP.match(/a=ice-ufrag:(\S+)/)?.[1] ?? ''
        const pwdLine   = localSDP.match(/a=ice-pwd:(\S+)/)?.[1]   ?? ''

        if (!candidate) {
          setErrorMsg('No valid ICE candidate found — check your network and try again.')
          setStatus('error')
          return
        }

        console.log('[Guest] Using candidate:', candidate)

        const compressed = encodeAnswer(candidate.ip, candidate.port, fpHex, ufragLine, pwdLine)
        if (compressed) {
          setWordCode(compressed)
          try {
            const u = new URL(window.location.href)
            u.searchParams.delete('offer')
            u.searchParams.delete('t')
            u.searchParams.set('answer', compressed)
            u.searchParams.set('for', guestMode)
            setAnswerUrl(u.toString())
          } catch {}
          try {
            const u = new URL(window.location.href)
            u.searchParams.delete('offer'); u.searchParams.delete('t')
            window.history.replaceState({}, '', u.toString())
          } catch {}
          setStatus('ready')
          return
        }

        setErrorMsg('Failed to generate connection code — no valid candidate found')
        setStatus('error')
      } catch (e) {
        if (!cancelled) { setErrorMsg(`Setup failed: ${e.message}`); setStatus('error') }
      }
    }
    run()
    return () => { cancelled = true }
  }, [encodedOffer, guestMode])

  const copyCode = () => {
    navigator.clipboard?.writeText(wordCode).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2500)
    }).catch(() => {})
  }

  const handleShare = async () => {
    const shareData = answerUrl
      ? { title: `Open ${modeLabel}`, url: answerUrl }
      : { title: `${modeLabel} Connection Code`, text: wordCode }
    if (navigator.share) {
      try {
        await navigator.share(shareData)
        setShared(true); setTimeout(() => setShared(false), 2500)
      } catch (err) { if (err.name !== 'AbortError') copyCode() }
    } else { copyCode() }
  }

  const Icon = ({ d, size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0,display:'block' }}>
      {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
  )
  const Icons = { qr: ['M3 3h7v7H3z','M14 3h7v7h-7z','M3 14h7v7H3z','M14 17h3v3h-3z','M17 14h3'] }

  if (status === 'error') return (
    <div style={hsRoot}><div style={hsCard}>
      <div style={hsBigIcon}>⚠️</div>
      <h2 style={hsH2}>Connection Error</h2>
      <p style={hsBody}>{errorMsg}</p>
      <p style={{ color: '#444', fontSize: 12, marginTop: 12 }}>Go back to Studio and generate a fresh link.</p>
    </div></div>
  )

  if (status === 'generating') return (
    <div style={hsRoot}><div style={hsCard}>
      <div style={hsBigIcon}>⚙️</div>
      <h2 style={hsH2}>Generating connection code…</h2>
      <p style={hsBody}>Please wait a few seconds</p>
      <Dots />
    </div></div>
  )

  return (
    <div style={hsRoot}>
      <div style={{ ...hsCard, maxWidth: 520 }}>
        <div style={{ fontSize: 28, marginBottom: 4 }}>📡 {modeLabel}</div>
        <h2 style={{ ...hsH2, fontSize: 17, marginBottom: 16 }}>
          Share or copy this code to Studio
        </h2>

        <div style={{ marginBottom: 16 }}>
          <button onClick={() => setShowQR(s => !s)} style={{ width:'100%',padding:'9px 12px',border:'1px solid #1a1a2e',borderRadius:8,color: showQR ? '#4eff91' : '#666',fontWeight:600,fontSize:13,cursor:'pointer',display:'flex',alignItems:'center',gap:'8px',justifyContent:'center',backgroundColor: showQR ? '#1a1a2e' : 'transparent' }}>
            <Icon d={Icons.qr} size={14} color={showQR ? '#4eff91' : '#666'} />
            {showQR ? 'Hide QR Code' : 'Show QR Code (for scanning)'}
          </button>
          {showQR && (
            <div style={{ backgroundColor: '#fff', padding: 20, borderRadius: 12, marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '3px solid #4eff91' }}>
              <QRCode value={wordCode} size={240} level="L" />
            </div>
          )}
          {showQR && <p style={{ fontSize: 11, color: '#444', margin: '8px 0 0', textAlign: 'center' }}>📱 Studio scans this with their phone camera</p>}
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize:11, color:'#555', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', textAlign:'left', marginBottom:6 }}>Connection Code ({wordCode.length} chars)</div>
          <div style={{ fontSize: 13, fontWeight: 700, fontFamily: 'monospace', color: '#4eff91', textAlign: 'center', padding: '16px 14px', backgroundColor: '#030d06', borderRadius: 12, border: '2px solid #0d3a20', userSelect: 'all', lineHeight: 1.6, letterSpacing: '0.03em', wordBreak: 'break-all' }}>
            {wordCode}
          </div>
          <p style={{ fontSize: 11, color: '#444', margin: '8px 0 0', textAlign: 'center' }}>✓ Copy and paste this into Studio</p>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {navigator.share && (
            <button onClick={handleShare} style={{ flex:1,padding:'11px 14px',border:'none',borderRadius:8,color:'#fff',fontWeight:700,fontSize:14,cursor:'pointer', backgroundColor: shared ? '#28a745' : '#6f42c1' }}>
              {shared ? '✅ Shared!' : '📤 Share'}
            </button>
          )}
          <button onClick={copyCode} style={{ flex:1,padding:'11px 14px',border:'none',borderRadius:8,color:'#fff',fontWeight:700,fontSize:14,cursor:'pointer', backgroundColor: copied ? '#28a745' : '#007bff' }}>
            {copied ? '✅ Copied!' : '📋 Copy'}
          </button>
        </div>

        <div style={{ padding:'10px 14px',backgroundColor:'#0d2e1a',border:'1px solid #28a74533',borderRadius:8,fontSize:12,color:'#28a745',lineHeight:1.6,textAlign:'left' }}>
          💡 In Studio → <strong>Connections</strong> → paste the code → <strong>Connect</strong>
        </div>
        <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
          <Dots />
          <span style={{ color: '#333', fontSize: 12 }}>Waiting for Studio to accept…</span>
        </div>
      </div>
    </div>
  )
}

const Dots = () => (
  <>
    <style>{`@keyframes hsDot{0%,100%{transform:scale(0.5);opacity:.2}50%{transform:scale(1);opacity:1}}`}</style>
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
      {[0,1,2].map(i => <div key={i} style={{ width:8,height:8,borderRadius:'50%',backgroundColor:'#e83e8c',animation:`hsDot 1.2s ease-in-out ${i*0.2}s infinite` }} />)}
    </div>
  </>
)

const hsRoot  = { minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',backgroundColor:'#0a0a0f',padding:24,fontFamily:'system-ui, sans-serif' }
const hsCard  = { backgroundColor:'#111',border:'1px solid #1e1e2e',borderRadius:16,padding:'32px 28px',width:'100%',maxWidth:420,textAlign:'center',color:'#fff' }
const hsBigIcon = { fontSize:48,marginBottom:16 }
const hsH2    = { margin:'0 0 8px',fontSize:22,fontWeight:800 }
const hsBody  = { margin:0,color:'#666',fontSize:14,lineHeight:1.6 }

// ─── StudioProvider ───────────────────────────────────────────────────────────

const makeSinglePeer = () => ({
  pc: null, dc: null, cdc: null, connected: false,
  shareUrl: null, encodedOffer: null, sessionKey: null,
  sameDevice: false, qrFits: false,
})

const INITIAL_STUDIO_STATE = {
  message: '', cue: '', lyrics: '', svgData: null,
}

export const StudioProvider = ({ children }) => {
  const mode = useMemo(() => {
    const p = new URLSearchParams(window.location.search)
    return p.get('mode') || MODES.STUDIO
  }, [])

  const wasOpenedWithOffer = useMemo(() => !!new URLSearchParams(window.location.search).get('offer'), [])
  const initialOffer       = useMemo(() => new URLSearchParams(window.location.search).get('offer') || null, [])

  const [pendingOffer,   setPendingOffer]   = useState(initialOffer)
  const [guestConnected, setGuestConnected] = useState(false)
  const [teleprompter,   setTeleprompter]   = useState(makeSinglePeer)
  const [remoteScroller, setRemoteScroller] = useState(makeSinglePeer)
  const [studioConnection, setStudioConnection] = useState({ pc: null, dc: null, connected: false })
  const [studioState,    setStudioState]    = useState(INITIAL_STUDIO_STATE)
  const [error,          setError]          = useState(null)

  const studioStateRef = useRef(INITIAL_STUDIO_STATE)
  const modeRef        = useRef(mode)
  const peerPCsRef     = useRef({})
  const peerDCsRef     = useRef({})
  const peerCDCsRef    = useRef({})

  const teleprompterPCRef   = useRef(null)
  const teleprompterDCRef   = useRef(null)
  const remoteScrollerDCRef = useRef(null)
  const studioDCRef         = useRef(null)

  const broadcastRef   = useRef(null)
  const listenersRef   = useRef([])

  useEffect(() => { modeRef.current = mode }, [mode])
  useEffect(() => { studioStateRef.current = studioState }, [studioState])

  const syncLegacyRefs = useCallback((targetMode, pc, dc, cdc) => {
    peerPCsRef.current[targetMode]  = pc
    peerDCsRef.current[targetMode]  = dc
    peerCDCsRef.current[targetMode] = cdc ?? null
    if (targetMode === MODES.TELEPROMPTER)         { teleprompterPCRef.current = pc; teleprompterDCRef.current = dc }
    else if (targetMode === MODES.REMOTE_SCROLLER) { remoteScrollerDCRef.current = dc }
  }, [])

  const clearLegacyRefs = useCallback((targetMode) => {
    delete peerPCsRef.current[targetMode]
    delete peerDCsRef.current[targetMode]
    delete peerCDCsRef.current[targetMode]
    if (targetMode === MODES.TELEPROMPTER)         { teleprompterPCRef.current = null; teleprompterDCRef.current = null }
    else if (targetMode === MODES.REMOTE_SCROLLER) { remoteScrollerDCRef.current = null }
  }, [])

  const onMessage  = useCallback((fn) => { if (!listenersRef.current.includes(fn)) listenersRef.current.push(fn) }, [])
  const offMessage = useCallback((fn) => { listenersRef.current = listenersRef.current.filter(l => l !== fn) }, [])
  const dispatch   = useCallback((data) => { listenersRef.current.forEach(fn => { try { fn(data) } catch {} }) }, [])

  const sendDC = useCallback((dc, payload) => {
    if (!dc || dc.readyState !== 'open') return
    const str = typeof payload === 'string' ? payload : JSON.stringify(payload)
    try { dc.send(str) } catch (e) { console.warn('[StudioP2P] sendDC:', e.message) }
  }, [])

  const broadcastAll = useCallback((payload) => {
    const str = JSON.stringify(payload)
    sendDC(teleprompterDCRef.current,   str)
    sendDC(remoteScrollerDCRef.current, str)
    if (broadcastRef.current && modeRef.current === MODES.STUDIO) {
      try { broadcastRef.current.postMessage(payload) } catch {}
    }
  }, [sendDC])

  const updateStudio = useCallback((patch) => {
    setStudioState(prev => {
      const next = { ...prev, ...patch }
      studioStateRef.current = next
      broadcastAll({ type: 'studioState', ...next })
      return next
    })
  }, [broadcastAll])

  const sendFullStateTo = useCallback((dc) => { sendDC(dc, JSON.stringify({ type: 'studioState', ...studioStateRef.current })) }, [sendDC])

  const sendScrollDelta = useCallback((delta) => {
    const str = JSON.stringify({ type: 'remote-scroll', delta })
    sendDC(teleprompterDCRef.current, str)
    if (broadcastRef.current && modeRef.current === MODES.STUDIO) {
      try { broadcastRef.current.postMessage({ type: 'remote-scroll', delta }) } catch {}
    }
  }, [sendDC])

  const applyStudioState = useCallback((rest) => {
    setStudioState(prev => { const next = { ...prev, ...rest }; studioStateRef.current = next; return next })
  }, [])

  const handleIncoming = useCallback((event) => {
    let parsed
    try { parsed = JSON.parse(event.data) } catch { dispatch({ type: 'raw', data: event.data }); return }

    if (modeRef.current === MODES.STUDIO) {
      if (parsed.type === 'requestState') {
        const allDCs = [teleprompterDCRef.current, remoteScrollerDCRef.current].filter(Boolean)
        allDCs.forEach(d => { if (d?.readyState === 'open') sendDC(d, JSON.stringify({ type: 'studioState', ...studioStateRef.current })) })
        return
      }
      if (parsed.type === 'remote-scroll' || parsed.type === 'remote-control') {
        sendDC(teleprompterDCRef.current, JSON.stringify(parsed))
        if (broadcastRef.current) { try { broadcastRef.current.postMessage(parsed) } catch {} }
        return
      }
      if (parsed.type === 'monitorMsg') { dispatch(parsed); return }
      dispatch(parsed); return
    }

    if (parsed.type === 'studioState')  { const { type: _t, ...rest } = parsed; applyStudioState(rest) }
    else                                { dispatch(parsed) }
  }, [dispatch, sendDC, applyStudioState])

  const attachChannel = useCallback((dc) => { dc.onmessage = handleIncoming }, [handleIncoming])

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return
    const bc = new BroadcastChannel('studio-broadcast')
    broadcastRef.current = bc

    bc.onmessage = (event) => {
      const data = event.data
      if (!data?.type) return

      if (modeRef.current !== MODES.STUDIO) {
        if (data.type === 'studioState')                                          { const { type: _t, ...rest } = data; applyStudioState(rest) }
        else if (data.type === 'remote-scroll' || data.type === 'remote-control') { dispatch(data) }
        else if (data.type === 'studio-dispatch')                                 { dispatch(data.payload) }
        return
      }

      if (data.type === 'view-connected') {
        setTimeout(() => { try { bc.postMessage({ type: 'studioState', ...studioStateRef.current }) } catch {} }, DELAYS.BC_ECHO)
        const { viewMode } = data
        if (viewMode === MODES.TELEPROMPTER) setTeleprompter(prev => ({ ...prev, connected: true, sameDevice: true }))
      } else if (data.type === 'view-disconnected') {
        if (data.viewMode === MODES.TELEPROMPTER) setTeleprompter(prev => prev.sameDevice ? { ...prev, connected: false, sameDevice: false } : prev)
      } else if (data.type === 'view-action') {
        const p = data.payload; if (!p) return
        if (p.type === 'remote-scroll' || p.type === 'remote-control') {
          sendDC(teleprompterDCRef.current, JSON.stringify(p))
          try { bc.postMessage(p) } catch {}
        } else { dispatch(p) }
      }
    }

    if (mode !== MODES.STUDIO && !wasOpenedWithOffer) {
      setStudioConnection(prev => ({ ...prev, connected: true }))
      setTimeout(() => { try { bc.postMessage({ type: 'view-connected', viewMode: mode }) } catch {} }, DELAYS.BC_ECHO)
      const onUnload = () => {
        try { const ch = new BroadcastChannel('studio-broadcast'); ch.postMessage({ type: 'view-disconnected', viewMode: mode }); setTimeout(() => ch.close(), 200) } catch {}
      }
      window.addEventListener('beforeunload', onUnload)
      return () => { window.removeEventListener('beforeunload', onUnload); bc.close(); broadcastRef.current = null }
    }
    return () => { bc.close(); broadcastRef.current = null }
  }, [mode, wasOpenedWithOffer, dispatch, sendDC, broadcastAll, applyStudioState])

  const buildOffer = useCallback(async (pc) => {
    const offer = await pc.createOffer({ offerToReceiveAudio: false, offerToReceiveVideo: false })
    await pc.setLocalDescription(offer)
    await waitForICEGathering(pc)
    const sdp          = pc.localDescription.sdp
    const encodedSDP   = encodeForQR(sdp)
    const finalEncoded = `o:${encodedSDP}`
    return { encoded: finalEncoded, qrFits: finalEncoded.length <= QR_SAFE_LIMIT }
  }, [])

  const setPeerByMode = useCallback((targetMode, updater) => {
    if (targetMode === MODES.TELEPROMPTER)         setTeleprompter(updater)
    else if (targetMode === MODES.REMOTE_SCROLLER) setRemoteScroller(updater)
  }, [])

  const createSingleOffer = useCallback(async (targetMode) => {
    let cancelled = false
    const isRemoteScroller = targetMode === MODES.REMOTE_SCROLLER
    const existingPC = peerPCsRef.current[targetMode]
    if (existingPC) { try { existingPC.close() } catch {} }
    clearLegacyRefs(targetMode)

    try {
      const pc  = createPeerConnection()
      const dc  = pc.createDataChannel('data', { ordered: true })
      const cdc = isRemoteScroller ? null : pc.createDataChannel('cues', { ordered: false, maxRetransmits: 0 })
      syncLegacyRefs(targetMode, pc, dc, cdc)

      // Add connection diagnostics
      pc.oniceconnectionstatechange = () => {
        console.log(`[${targetMode} ICE] ${pc.iceConnectionState}`)
        if (pc.iceConnectionState === 'connected') {
          logConnectionDiagnostics(pc, `${targetMode} connected`)
        }
      }

      const onChannelsOpen = async () => {
        if (cancelled) return
        if (isRemoteScroller) {
          dc.addEventListener('message', (event) => {
            let parsed; try { parsed = JSON.parse(event.data) } catch { return }
            if (parsed.type === 'remote-scroll' || parsed.type === 'remote-control') {
              sendDC(teleprompterDCRef.current, JSON.stringify(parsed))
              if (broadcastRef.current && modeRef.current === MODES.STUDIO) { try { broadcastRef.current.postMessage(parsed) } catch {} }
            } else { dispatch(parsed) }
          })
          setPeerByMode(targetMode, prev => ({ ...prev, connected: true, dc }))
          return
        }
        attachChannel(dc)
        if (cdc) attachChannel(cdc)
        setPeerByMode(targetMode, prev => ({ ...prev, connected: true, dc, cdc }))
        setTimeout(() => { if (!cancelled) sendFullStateTo(dc) }, DELAYS.SEND_FULL_STATE)
      }

      dc.onopen = () => {
        if (peerDCsRef.current[targetMode] !== dc) return
        if (isRemoteScroller) { onChannelsOpen(); return }
        waitBothChannels(dc, cdc).then(onChannelsOpen)
      }
      dc.onclose = () => {
        if (peerDCsRef.current[targetMode] !== dc) return
        clearLegacyRefs(targetMode)
        setPeerByMode(targetMode, prev => ({ ...prev, connected: false, dc: null, cdc: null }))
      }
      pc.onconnectionstatechange = () => {
        if (peerPCsRef.current[targetMode] !== pc) return
        if (pc.connectionState === 'failed') { try { pc.restartIce() } catch {} }
        if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') setPeerByMode(targetMode, prev => ({ ...prev, connected: false }))
      }

      const { encoded, qrFits } = await buildOffer(pc)
      if (cancelled) { try { pc.close() } catch {}; return }
      const shareUrl = `${window.location.origin}${window.location.pathname}?mode=${targetMode}&offer=${encodeURIComponent(encoded)}`
      setPeerByMode(targetMode, prev => ({ ...prev, pc, dc, connected: false, shareUrl, encodedOffer: encoded, qrFits }))
    } catch (e) {
      if (!cancelled) setError(`${targetMode}: failed — ${e.message}`)
    }
    return () => { cancelled = true }
  }, [clearLegacyRefs, syncLegacyRefs, attachChannel, buildOffer, dispatch, sendDC, sendFullStateTo, setPeerByMode])

  const acceptSingleAnswer = useCallback(async (targetMode, rawInput) => {
    const trimmed = rawInput.trim()
    const pc      = peerPCsRef.current[targetMode]
    if (!pc) { setError(`${targetMode}: no active offer — generate a new link first`); return }

    let sdp = null

    if (trimmed.startsWith('v') && trimmed.includes('_')) {
      const decoded = decodeAnswer(trimmed)
      if (decoded) {
        console.log(`[${targetMode}] Decoded answer:`, decoded)
        const localSDP = pc.localDescription?.sdp
        if (!localSDP) { setError('No local SDP available — regenerate the offer'); return }
        const mids   = extractMidsFromSDP(localSDP)
        const mlines = extractMlinesFromSDP(localSDP)
        try {
          sdp = reconstructAnswerSDP({
            ufrag:          decoded.ufrag,
            pwd:            decoded.pwd,
            ip:             decoded.ip,
            port:           decoded.port,
            fingerprintHex: decoded.fingerprintHex,
            mids,
            mlines,
          })
        } catch (err) { setError(`Failed to reconstruct SDP: ${err.message}`); return }
      }
    }

    if (!sdp) {
      try {
        const url = new URL(trimmed)
        if (url.origin === window.location.origin) {
          const answerCode = url.searchParams.get('answer')
          if (answerCode) return acceptSingleAnswer(targetMode, answerCode)
        }
      } catch {}

      if (trimmed.startsWith('a:') || trimmed.startsWith('o:')) {
        sdp = decodeFromQR(trimmed.slice(2))
      } else if (trimmed.startsWith('v=')) {
        sdp = trimmed
      } else {
        const legacy = decompress(trimmed)
        if (legacy?.sdp) sdp = legacy.sdp
      }
    }

    if (!sdp) { setError('Invalid connection code — please check and try again'); return }

    try {
      await pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp }))
      console.log(`[${targetMode}] Answer accepted - waiting for connection...`)
    } catch (e) {
      setError(`${targetMode}: ${e.message}`)
    }
  }, [])

  const sendMessageFromView = useCallback((payload) => {
    if (studioDCRef.current?.readyState === 'open') { sendDC(studioDCRef.current, payload); return }
    if (broadcastRef.current) {
      try {
        const parsed = typeof payload === 'string' ? JSON.parse(payload) : payload
        broadcastRef.current.postMessage({ type: 'view-action', payload: parsed })
      } catch {}
    }
  }, [sendDC])

  useEffect(() => {
    if (!error) return
    const t = setTimeout(() => setError(null), DELAYS.ERROR_CLEAR)
    return () => clearTimeout(t)
  }, [error])

  const clearError = useCallback(() => setError(null), [])

  useEffect(() => {
    return () => {
      listenersRef.current = []
      Object.values(peerPCsRef.current).forEach(pc   => { try { pc?.close()  } catch {} })
      Object.values(peerDCsRef.current).forEach(dc   => { try { dc?.close()  } catch {} })
      Object.values(peerCDCsRef.current).forEach(cdc => { try { cdc?.close() } catch {} })
      try { studioDCRef.current?.close() } catch {}
    }
  }, [])

  const effectiveStudioConnection = wasOpenedWithOffer
    ? { pc: null, dc: studioDCRef.current, connected: guestConnected }
    : studioConnection

  const handleGuestConnected = useCallback(({ pc, dc }) => {
    studioDCRef.current = dc
    attachChannel(dc)
    dc.onclose = () => { setGuestConnected(false) }
    setTimeout(() => { try { dc.send(JSON.stringify({ type: 'requestState' })) } catch {} }, 200)
    setGuestConnected(true)
    setPendingOffer(null)
  }, [attachChannel])

  const needsHandshake = !!pendingOffer && mode !== MODES.STUDIO

  return (
    <StudioContext.Provider value={{
      mode, teleprompter, remoteScroller,
      studioConnection: effectiveStudioConnection,
      studioState, error, MODES,
      createSingleOffer, acceptSingleAnswer,
      updateStudio,
      sendMessage: sendMessageFromView, sendScrollDelta,
      onMessage, offMessage,
      clearError,
    }}>
      {needsHandshake
        ? <GuestHandshake
            encodedOffer={pendingOffer}
            guestMode={mode}
            onConnected={handleGuestConnected}
          />
        : children
      }
    </StudioContext.Provider>
  )
}